import React, { useCallback, useState } from 'react';

import { getEditorWorkflowRoute, Select } from '@aline/common';
import { DndContext, DragOverlay, useDraggable, useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CircularProgress, IconButton, Skeleton, Typography } from '@mui/material';
import clsx from 'clsx';
import {
  CirclePlus,
  CloudUpload,
  Download,
  FileIcon,
  GripVertical,
  Lock,
  Signature,
  SquarePen,
  Trash2,
  UploadIcon,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAccessGuard } from 'src/components/auth';
import { UploadFilesPicker } from 'src/components/FilestackPicker';
import { PanelHeader } from 'src/components/generic';
import { AlineButton } from 'src/components/generic/AlineButton';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import { ConfirmationDialog } from 'src/components/generic/ConfirmationDialog';
import { InlineEditInput } from 'src/components/generic/InlineEditInput';
import { Spacer } from 'src/components/generic/Spacer';
import { IconColors } from 'src/components/icons/icon-utils';
import type { StorableIconDocument } from 'src/components/StorableIcon';
import { StorableIcon } from 'src/components/StorableIcon';
import { AddWorkflowFilesMenu } from 'src/components/workflow/AddWorkflowFilesMenu';
import { useIsViewer } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { openLinkFromAnchorClick } from 'src/helpers';
import { FileStackDomContainer } from 'src/helpers/integrations/filestack';
import { useCheckWorkflowAuthorization } from 'src/hooks';
import { useCreatePhase } from 'src/hooks/phase';
import { useExportWorkflowFiles } from 'src/hooks/workflow/workflow';
import { UploadVersion } from 'src/version-history/UploadVersion';
import { getWorkflowWasSentForSig } from 'src/workflow';
import { makeStyles } from 'tss-react/mui';

import { useDocumentDnd } from './hooks/use-document-dnd';
import { useDocumentRename } from './hooks/use-document-rename';
import { useDocumentDropzone } from './use-document-dropzone';
import { WorkflowFileLockButton } from './WorkflowFileLockButton';
import { useWorkflowDetailLinks } from '../WorkflowDetailContext';

const useStyles = makeStyles()(theme => ({
  createSigningBody: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    justifyContent: 'center',
    padding: theme.spacing(2, 2),
    width: '100%',
  },
  createSigningButton: {
    borderColor: theme.appColors.primary ?? '#146ee8',
    borderRadius: 40,
    color: theme.appColors.primary ?? '#146ee8',
    fontSize: 12,
    fontWeight: 'bold',
    height: 24,
    padding: theme.spacing(0, 3),
  },
  createSigningText: {
    color: '#324a52',
    fontSize: 12,
    textAlign: 'center',
  },
  fileCountBadge: {
    alignItems: 'center',
    backgroundColor: '#7ab2fe',
    borderRadius: 4,
    color: '#fff',
    display: 'flex',
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 600,
    height: 15,
    justifyContent: 'center',
    minWidth: 16,
    padding: theme.spacing(0, 0.5),
  },
  root: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    overflow: 'hidden',
  },
  section: {
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    background: '#FFF',
    border: '1px solid #E2E8F0',
    borderRadius: 12,
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    gap: theme.spacing(1),
    padding: theme.spacing(2, 0),
  },
  sectionDropTarget: {
    transition: 'border-color 0.2s, background-color 0.2s',
  },
  sectionDropTargetActive: {
    backgroundColor: theme.palette.primary.main + '0A',
    borderColor: theme.palette.primary.main,
  },
  sectionHeader: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-start',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    width: '100%',
  },
  sectionSubtitleText: {
    boxSizing: 'border-box',
    color: '#324a52',
    fontSize: 12,
    fontWeight: 600,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    width: '100%',
  },
  sections: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(1),
  },
  title: {
    alignItems: 'center',
    color: theme.appColors.iconsType,
    display: 'flex',
    fontSize: 16,
    fontWeight: 'bold',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
}));

const useDocumentListStyles = makeStyles<void, 'actions'>()((theme, _params, classes) => ({
  actionButton: {
    '&:hover': { color: theme.palette.text.primary },
    color: theme.palette.text.secondary,
    flexShrink: 0,
    padding: 2,
  },
  // Out of the layout until the row is hovered or focused, so the name gets the full width.
  actions: {
    alignItems: 'center',
    display: 'none',
    flexShrink: 0,
    gap: 4,
  },
  actionsWrapper: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: 4,
    /**
     * The document link's stretched `::after` covers the whole row, so every control in this
     * cluster has to sit above it to stay clickable. Lifting the wrapper rather than each
     * control means anything added here later is covered too.
     */
    position: 'relative',
    zIndex: 2,
  },
  documentName: {
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    display: '-webkit-box',
    overflow: 'hidden',
    overflowWrap: 'break-word',
  },
  documentNameClickable: {
    cursor: 'pointer',
    flex: 1,
    minWidth: 0,
  },
  documentNameInput: {
    color: '#324a52',
    fontSize: theme.typography.body2.fontSize,
    lineHeight: theme.typography.body2.lineHeight,
    padding: 0,
  },
  documentNameInputSigning: {
    color: '#146ee8',
    fontSize: theme.typography.body2.fontSize,
    fontWeight: 700,
    lineHeight: theme.typography.body2.lineHeight,
    padding: 0,
  },
  documentNameLink: {
    color: 'inherit',
    textDecoration: 'none',
  },
  documentNameSigning: {
    color: '#146ee8',
  },
  documentRow: {
    /**
     * The document link's click target is the whole row via its stretched `::after`, so the
     * focus ring belongs on the row rather than on the link's own text.
     */
    '&:has(a:focus-visible)': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
    '&:hover': { backgroundColor: '#f8f9fa' },
    [`&:hover .${classes.actions}, &:focus-within .${classes.actions}`]: { display: 'flex' },
    alignItems: 'stretch',
    borderRadius: 8,
    boxSizing: 'border-box',
    display: 'flex',
    padding: theme.spacing(0.5, 0),
    // Containing block for the document link's stretched ::after.
    position: 'relative',
    width: '100%',
  },
  documentRowContent: {
    boxSizing: 'border-box',
    display: 'flex',
    flex: 1,
    minWidth: 0,
    paddingRight: theme.spacing(2),
  },
  documentRowGeneral: {
    backgroundColor: '#f8f9fa',
  },
  documentRowLead: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    gap: theme.spacing(0.75),
    minWidth: 0,
  },
  documentRowMain: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  },
  dragHandle: {
    '&:active': { cursor: 'grabbing' },
    alignItems: 'center',
    alignSelf: 'center',
    color: theme.palette.text.secondary,
    cursor: 'grab',
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'center',
    touchAction: 'none',
  },
  dropzone: {
    alignItems: 'center',
    border: '1px dashed #E2E8F0',
    borderRadius: 12,
    boxSizing: 'border-box',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    padding: theme.spacing(2),
    transition: 'border-color 0.2s, background-color 0.2s',
    width: '100%',
  },
  dropzoneActive: {
    backgroundColor: theme.palette.primary.main + '0A',
    borderColor: theme.palette.primary.main,
  },
  dropzoneEmptyOuter: {
    boxSizing: 'border-box',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    width: '100%',
  },
  handleSlot: {
    alignItems: 'center',
    alignSelf: 'stretch',
    boxSizing: 'border-box',
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'center',
    // Above the document link's stretched ::after, so the row stays draggable.
    position: 'relative',
    width: theme.spacing(2),
    zIndex: 2,
  },
  innerEmpty: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    justifyContent: 'center',
  },
  orderBadge: {
    alignItems: 'center',
    background: 'rgba(50, 74, 82, 0.12)',
    borderRadius: 20,
    color: '#324a52',
    display: 'flex',
    flexShrink: 0,
    fontSize: 10,
    fontWeight: 700,
    height: 15,
    justifyContent: 'center',
    minWidth: 16,
    padding: theme.spacing(0, 0.5),
  },
  overlayLock: {
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  root: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    justifyContent: 'center',
    width: '100%',
  },
  /**
   * The link carries the document name so it has real link text, and its `::after` stretches
   * over the whole row so clicking anywhere in the row still opens the document. Keeping the
   * text inside the anchor (rather than using an empty overlay) is what lets the row's own
   * click target be the link itself.
   */
  stretchedLink: {
    '&::after': {
      content: '""',
      inset: 0,
      position: 'absolute',
      zIndex: 1,
    },
    // The visible ring is drawn on the row via `:has(a:focus-visible)`.
    '&:focus-visible': { outline: 'none' },
    color: 'inherit',
    minWidth: 0,
    textDecoration: 'none',
  },
  uploadIconWrapper: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 16,
  },
}));

type WorkflowFileForList = NonNullable<
  NonNullable<Apollo.GetWorkflowFilesQuery['workflow']>['files']
>[number];

interface DocumentsPanelProps {
  workflowId: UUID;
}

export const DocumentsPanel = React.memo(function DocumentsPanel({
  workflowId,
}: DocumentsPanelProps) {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const links = useWorkflowDetailLinks(workflowId);
  const {
    activeFile,
    activeId,
    dndHandlers,
    generalFiles,
    loading,
    overContainerId,
    sensors,
    signingFiles,
    sortableSigningIds,
    workflowWithFiles,
  } = useDocumentDnd(workflowId);

  const { data: phasesData } = Apollo.useGetWorkflowPhasesQuery({ variables: { workflowId } });
  const hasSigningPhase =
    phasesData?.workflow?.phases?.some(p => p.__typename === 'SigningPhase') ?? false;
  const isTemplate = workflowWithFiles?.variant === Apollo.WorkflowVariant.Template;
  const documentVariant = isTemplate
    ? Apollo.DocumentVariant.Template
    : Apollo.DocumentVariant.Document;

  if (loading || !workflowWithFiles)
    return (
      <div className={classes.root}>
        <PanelHeader
          showBackButton
          text="Documents"
          onClickBack={() => navigate(links.baseUrl())}
        />
        <div className={classes.sections}>
          <Skeleton height={120} variant="rectangular" width="100%" />
          <Skeleton height={120} variant="rectangular" width="100%" />
        </div>
      </div>
    );

  return (
    <div className={classes.root}>
      <PanelHeader showBackButton text="Documents" onClickBack={() => navigate(links.baseUrl())} />
      <div className={classes.sections}>
        <DndContext sensors={sensors} {...dndHandlers}>
          <DocumentSection
            activeId={activeId}
            documentRole={Apollo.WorkflowFileDocumentRole.General}
            documentVariant={documentVariant}
            files={generalFiles}
            icon={<FileIcon size={16} />}
            overContainerId={overContainerId}
            title="General"
            workflowWithFiles={workflowWithFiles}
          />
          <DocumentSection
            activeId={activeId}
            documentRole={Apollo.WorkflowFileDocumentRole.Signing}
            documentVariant={documentVariant}
            files={signingFiles}
            hasSigningPhase={hasSigningPhase}
            icon={<Signature size={16} />}
            overContainerId={overContainerId}
            sortableIds={sortableSigningIds}
            title="Signing"
            workflowWithFiles={workflowWithFiles}
          />
          {/**
           * The default drop animation flies the overlay back to the dragged row's old
           * position, because the list only reorders once the server confirms the new
           * order — so a successful drop reads as a rejected one.
           */}
          <DragOverlay dropAnimation={null}>
            {activeFile ? <DocumentDragOverlayCard file={activeFile} /> : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
});

interface DocumentSectionProps {
  activeId: string | null;
  documentRole: Apollo.WorkflowFileDocumentRole;
  documentVariant: Apollo.DocumentVariant;
  files: WorkflowFileForList[];
  icon: React.ReactNode;
  overContainerId: Apollo.WorkflowFileDocumentRole | null;
  title: string;
  workflowWithFiles: NonNullable<Apollo.GetWorkflowFilesQuery['workflow']>;
  hasSigningPhase?: boolean;
  sortableIds?: string[];
}

function DocumentSection({
  activeId,
  documentRole,
  documentVariant,
  files,
  hasSigningPhase,
  icon,
  overContainerId,
  sortableIds,
  title,
  workflowWithFiles,
}: DocumentSectionProps) {
  const isViewer = useIsViewer();
  const { classes } = useStyles();
  const { setNodeRef } = useDroppable({ id: documentRole });
  const [isFilePickerOpen, setFilePickerOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [addDocumentsToWorkflow] = Apollo.useAddDocumentsToWorkflowMutation();
  const { canEditWorkflow, workflow } = useCheckWorkflowAuthorization(workflowWithFiles.id);
  const handleOpenAddDocumentMenu = useAccessGuard({
    callback: (anchor: HTMLElement) => setMenuAnchor(anchor),
    minRole: Apollo.OrgRole.Basic,
  });
  const handleSelectFiles = useAccessGuard({
    callback: () => setFilePickerOpen(true),
    minRole: Apollo.OrgRole.Basic,
  });
  const workflowSentForSig = getWorkflowWasSentForSig(workflowWithFiles);
  const canMoveBetweenRoles = Boolean(canEditWorkflow && !workflowSentForSig && !isViewer);
  /**
   * True while a file from the other section hovers this one — whether over the section
   * backdrop or one of its rows. `useDroppable`'s `isOver` only covers the backdrop, so the
   * highlight would drop out as soon as the cursor crossed a row.
   */
  const isCrossGroupTarget = Boolean(
    overContainerId === documentRole && activeId && files.every(f => f.id !== activeId)
  );
  const isSigningFiles = documentRole === Apollo.WorkflowFileDocumentRole.Signing;
  const needsSigningPhase = hasSigningPhase === false;
  const hideAddForSigning = needsSigningPhase || (isSigningFiles && workflowSentForSig);
  const { exportToPdf, loading: exportLoading } = useExportWorkflowFiles(workflowWithFiles.id);
  const { dropzoneProps, isDragging, isUploading, versionDialog } = useDocumentDropzone({
    attachedDocumentIds: workflowWithFiles.files.map(file => file.document.id),
    disabled: !canEditWorkflow || hideAddForSigning || isViewer,
    documentVariant,
    workflowId: workflowWithFiles.id,
  });
  const showDownload = files.length > 0;

  return (
    <>
      <div
        ref={setNodeRef}
        className={clsx(
          classes.section,
          classes.sectionDropTarget,
          isCrossGroupTarget && !hideAddForSigning && classes.sectionDropTargetActive
        )}
      >
        <div className={classes.sectionHeader}>
          {icon}
          <Typography variant="subtitle2">{title}</Typography>
          <span className={classes.fileCountBadge}>{files.length}</span>
          <Spacer />
          {showDownload && (
            <AlineTooltip arrow placement="top" title="Download documents">
              <IconButton
                disabled={exportLoading}
                size="small"
                sx={{ color: 'text.primary' }}
                onClick={exportToPdf}
              >
                <Download size={18} />
              </IconButton>
            </AlineTooltip>
          )}
          {!hideAddForSigning && (
            <AlineTooltip
              arrow
              placement="top"
              title={
                documentRole === Apollo.WorkflowFileDocumentRole.Signing
                  ? 'Add signing document'
                  : 'Add general document'
              }
            >
              <span>
                <IconButton
                  disabled={!canEditWorkflow}
                  size="small"
                  sx={{ color: 'text.primary' }}
                  onClick={e => handleOpenAddDocumentMenu(e.currentTarget)}
                >
                  <CirclePlus size={18} />
                </IconButton>
              </span>
            </AlineTooltip>
          )}
        </div>
        {isSigningFiles && !needsSigningPhase && (
          <Typography className={classes.sectionSubtitleText} variant="body2">
            The order of documents determines the order they are signed.
          </Typography>
        )}
        <DocumentSectionBody
          activeId={activeId}
          canAddDocuments={canEditWorkflow}
          canMoveBetweenRoles={canMoveBetweenRoles}
          documentVariant={documentVariant}
          dropzoneProps={dropzoneProps}
          files={files}
          isDragging={isDragging}
          isUploading={isUploading}
          needsSigningPhase={needsSigningPhase}
          sortableIds={sortableIds}
          workflowId={workflowWithFiles.id}
          onSelectFiles={handleSelectFiles}
        />
      </div>
      {versionDialog}
      <AddWorkflowFilesMenu
        anchorEl={menuAnchor}
        documentRole={documentRole}
        open={Boolean(menuAnchor)}
        workflow={workflow}
        workflowId={workflowWithFiles.id}
        onClose={() => setMenuAnchor(null)}
      />
      <UploadFilesPicker
        domContainer={FileStackDomContainer.SendForSignaturePage}
        open={isFilePickerOpen}
        pickerOpts={{
          afterUploadDone: async res => {
            setFilePickerOpen(false);
            const documentIds = res.data?.createFiles?.map(file => file.id) ?? [];
            if (documentIds.length)
              await addDocumentsToWorkflow({
                variables: {
                  input: { documentIds, documentRole, workflowId: workflowWithFiles.id },
                },
              });
          },
          onClose: () => setFilePickerOpen(false),
          props: { documentVariant, isSigned: false },
        }}
      />
    </>
  );
}

interface DocumentSectionBodyProps extends DocumentListProps {
  needsSigningPhase: boolean;
  workflowId: UUID;
}

function DocumentSectionBody({
  canAddDocuments,
  dropzoneProps,
  needsSigningPhase,
  workflowId,
  ...rest
}: DocumentSectionBodyProps) {
  if (needsSigningPhase && canAddDocuments) {
    return (
      <div {...dropzoneProps} style={{ width: '100%' }}>
        <CreateSigningPhasePrompt workflowId={workflowId} />
      </div>
    );
  }

  return (
    <DocumentList
      canAddDocuments={canAddDocuments}
      dropzoneProps={dropzoneProps}
      workflowId={workflowId}
      {...rest}
    />
  );
}

function CreateSigningPhasePrompt({ workflowId }: { workflowId: UUID }) {
  const { classes } = useStyles();
  const { createPhase } = useCreatePhase();

  const handleCreate = useCallback(async () => {
    await createPhase({
      variables: { input: { isSigning: true, name: 'Signing', workflowId } },
    });
  }, [createPhase, workflowId]);

  return (
    <div className={classes.createSigningBody}>
      <Typography className={classes.createSigningText}>
        Create a signing phase to add documents
      </Typography>
      <AlineButton
        className={classes.createSigningButton}
        color="primary"
        variant="outlined"
        onClick={handleCreate}
      >
        Create signing phase
      </AlineButton>
    </div>
  );
}

interface DocumentListProps {
  activeId: string | null;
  canAddDocuments: boolean;
  canMoveBetweenRoles: boolean;
  documentVariant: Apollo.DocumentVariant;
  dropzoneProps: ReturnType<typeof useDocumentDropzone>['dropzoneProps'];
  files: WorkflowFileForList[];
  isDragging: boolean;
  isUploading: boolean;
  onSelectFiles: () => void;
  workflowId: UUID;
  sortableIds?: string[];
}

function DocumentList({
  activeId,
  canAddDocuments,
  canMoveBetweenRoles,
  documentVariant,
  dropzoneProps,
  files,
  isDragging,
  isUploading,
  onSelectFiles,
  sortableIds,
  workflowId,
}: DocumentListProps) {
  const { classes } = useDocumentListStyles();

  if (!files.length)
    return (
      <div className={classes.dropzoneEmptyOuter}>
        <div
          {...dropzoneProps}
          className={clsx(classes.dropzone, { [classes.dropzoneActive]: isDragging })}
        >
          <DropzoneEmptyContent
            isUploading={isUploading}
            showSelectFiles={canAddDocuments}
            onSelectFiles={onSelectFiles}
          />
        </div>
      </div>
    );

  const rows = files.map(file => (
    <DocumentRowForMode
      key={file.id}
      activeId={activeId}
      canMoveBetweenRoles={canMoveBetweenRoles}
      documentVariant={documentVariant}
      file={file}
      sortableIds={sortableIds}
      workflowId={workflowId}
    />
  ));

  return (
    <div {...dropzoneProps} className={classes.root}>
      {sortableIds ? (
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          {rows}
        </SortableContext>
      ) : (
        rows
      )}
    </div>
  );
}

function DocumentRowClickable({
  file,
  workflowId,
}: {
  file: WorkflowFileForList;
  workflowId: UUID;
}) {
  const { classes } = useDocumentListStyles();
  const documentHref = getEditorWorkflowRoute(file.document.id, workflowId);

  return (
    <div className={classes.documentRow}>
      <div aria-hidden className={classes.handleSlot} />
      <div className={classes.documentRowContent}>
        <div className={classes.documentRowMain}>
          <Link
            className={clsx(
              classes.documentName,
              classes.documentNameClickable,
              classes.documentRowLead,
              classes.documentNameLink
            )}
            data-cy={
              file.document.isCertificate
                ? undefined
                : Select.workflow.detail.documents.documentName
            }
            style={{ alignItems: 'center', display: 'flex', gap: 6 }}
            to={documentHref}
            onClick={e => openLinkFromAnchorClick(e, documentHref)}
          >
            <StorableIcon iconProps={{ height: 14, width: 14 }} storable={file.document} />
            <Typography className={classes.documentName} variant="body2">
              {file.document.name}
            </Typography>
          </Link>
          <div className={classes.actionsWrapper}>
            <FileLockSlot file={file} workflowId={workflowId} />
          </div>
        </div>
      </div>
    </div>
  );
}

// A locked file always shows its lock; an unlocked one offers it on row hover.
function FileLockSlot({ file, workflowId }: { file: WorkflowFileForList; workflowId: UUID }) {
  const { classes } = useDocumentListStyles();
  const lockButton = (
    <WorkflowFileLockButton className={classes.actionButton} file={file} workflowId={workflowId} />
  );

  if (file.isInternal) return lockButton;
  return <div className={classes.actions}>{lockButton}</div>;
}

function DocumentDragOverlayCard({ file }: { file: WorkflowFileForList }) {
  const { classes } = useDocumentListStyles();
  const orderNum = parseInt(file.orderId);
  const isSigning = file.documentRole === Apollo.WorkflowFileDocumentRole.Signing;

  return (
    <div
      className={classes.documentRow}
      style={{
        background: '#fff',
        boxShadow: '0px 4px 16px rgba(0,0,0,0.18)',
        pointerEvents: 'none',
      }}
    >
      <div className={classes.handleSlot}>
        <div className={classes.dragHandle}>
          <GripVertical size={14} />
        </div>
      </div>
      <div className={classes.documentRowContent}>
        <div className={classes.documentRowMain}>
          <div className={classes.documentRowLead}>
            {isSigning && !Number.isNaN(orderNum) && (
              <div className={classes.orderBadge}>{orderNum}</div>
            )}
            <StorableIcon iconProps={{ height: 14, width: 14 }} storable={file.document} />
            <Typography
              className={`${classes.documentName} ${isSigning ? classes.documentNameSigning : ''}`}
              style={isSigning ? { fontWeight: 700 } : undefined}
              variant="body2"
            >
              {file.document.name}
            </Typography>
          </div>
          {file.isInternal && (
            <Lock aria-label="Internal" className={classes.overlayLock} size={14} />
          )}
        </div>
      </div>
    </div>
  );
}

interface DocumentRowProps {
  documentVariant: Apollo.DocumentVariant;
  file: WorkflowFileForList;
  workflowId: UUID;
}

function DocumentRowActions({
  file,
  onStartRename,
  workflowId,
}: Pick<DocumentRowProps, 'file' | 'workflowId'> & {
  onStartRename: () => void;
}) {
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [removeDocumentsFromWorkflow] = Apollo.useRemoveDocumentsFromWorkflowMutation();
  const { classes } = useDocumentListStyles();

  const handleDelete = useCallback(() => {
    void removeDocumentsFromWorkflow({
      variables: { input: { documentIds: [file.document.id], workflowId } },
    });
    setConfirmRemoveOpen(false);
  }, [removeDocumentsFromWorkflow, file.document.id, workflowId]);

  return (
    <div className={classes.actionsWrapper}>
      <div className={classes.actions}>
        <AlineTooltip arrow placement="top" title="Rename">
          <IconButton className={classes.actionButton} size="small" onClick={onStartRename}>
            <SquarePen size={14} />
          </IconButton>
        </AlineTooltip>
        <AlineTooltip arrow placement="top" title="Remove from workflow">
          <IconButton
            className={classes.actionButton}
            size="small"
            onClick={() => setConfirmRemoveOpen(true)}
          >
            <Trash2 size={14} />
          </IconButton>
        </AlineTooltip>
      </div>
      <FileLockSlot file={file} workflowId={workflowId} />
      <span data-cy={Select.workflow.detail.documents.uploadVersionButton}>
        <UploadVersion
          documentId={file.document.id}
          icon={<UploadIcon size={14} />}
          iconWrapperClass={classes.uploadIconWrapper}
        />
      </span>
      <ConfirmationDialog
        message={`Remove "${file.document.name}" from this workflow?`}
        open={confirmRemoveOpen}
        primaryText="Remove"
        title="Remove from workflow"
        onClickPrimary={handleDelete}
        onClose={() => setConfirmRemoveOpen(false)}
      />
    </div>
  );
}

interface DraggableDocumentRowProps extends DocumentRowProps {
  isBeingDragged: boolean;
}

function DraggableDocumentRow({
  documentVariant: _documentVariant,
  file,
  isBeingDragged,
  workflowId,
}: DraggableDocumentRowProps) {
  const { classes } = useDocumentListStyles();
  const { commitRename, isRenaming, renameValue, setIsRenaming, setRenameValue, startRename } =
    useDocumentRename(file);
  const { attributes, listeners, setNodeRef } = useDraggable({ id: file.id });

  return (
    <div
      ref={setNodeRef}
      aria-label="Drag to move document"
      className={classes.documentRow}
      style={{ opacity: isBeingDragged ? 0.3 : undefined }}
    >
      <div className={classes.handleSlot}>
        <div className={classes.dragHandle} {...listeners} {...attributes}>
          <GripVertical size={14} />
        </div>
      </div>
      <div className={classes.documentRowContent}>
        <div className={classes.documentRowMain}>
          <div className={classes.documentRowLead}>
            <EditableDocumentName
              commitRename={commitRename}
              fileDocument={file.document}
              includeWorkflowDocumentNameCy={!file.document.isCertificate}
              isRenaming={isRenaming}
              linkTo={getEditorWorkflowRoute(file.document.id, workflowId)}
              name={file.document.name}
              renameValue={renameValue}
              setIsRenaming={setIsRenaming}
              setRenameValue={setRenameValue}
            />
          </div>
          <DocumentRowActions file={file} workflowId={workflowId} onStartRename={startRename} />
        </div>
      </div>
    </div>
  );
}

function SortableDocumentRow({
  documentVariant: _documentVariant,
  file,
  workflowId,
}: DocumentRowProps) {
  const { classes } = useDocumentListStyles();
  const { commitRename, isRenaming, renameValue, setIsRenaming, setRenameValue, startRename } =
    useDocumentRename(file);
  const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({
    /**
     * Layout changes come from the optimistic order write, not from dnd-kit — suppress
     * the post-drop "slide home" that would otherwise fight that re-render.
     */
    animateLayoutChanges: () => false,
    id: file.id,
  });

  const orderNum = parseInt(file.orderId);
  const style = {
    opacity: isDragging ? 0.3 : undefined,
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      aria-label="Drag to reorder document"
      className={classes.documentRow}
      style={style}
    >
      <div className={classes.handleSlot}>
        <div className={classes.dragHandle} {...listeners} {...attributes}>
          <GripVertical size={14} />
        </div>
      </div>
      <div className={classes.documentRowContent}>
        <div className={classes.documentRowMain}>
          <div className={classes.documentRowLead}>
            {!Number.isNaN(orderNum) && <div className={classes.orderBadge}>{orderNum}</div>}
            <EditableDocumentName
              isSigning
              commitRename={commitRename}
              fileDocument={file.document}
              isRenaming={isRenaming}
              linkTo={getEditorWorkflowRoute(file.document.id, workflowId)}
              name={file.document.name}
              renameValue={renameValue}
              setIsRenaming={setIsRenaming}
              setRenameValue={setRenameValue}
            />
          </div>
          <DocumentRowActions file={file} workflowId={workflowId} onStartRename={startRename} />
        </div>
      </div>
    </div>
  );
}

function DropzoneEmptyContent({
  isUploading,
  onSelectFiles,
  showSelectFiles,
}: {
  isUploading: boolean;
  onSelectFiles: () => void;
  showSelectFiles: boolean;
}) {
  const { classes } = useDocumentListStyles();

  if (isUploading) {
    return (
      <div className={classes.innerEmpty}>
        <CircularProgress size={48} />
        <Typography variant="body1">Uploading...</Typography>
      </div>
    );
  }

  return (
    <div className={classes.innerEmpty}>
      <CloudUpload color={IconColors.Primary} opacity={0.6} size={48} />
      <Typography variant="body1">
        {showSelectFiles ? 'Drag and drop items or' : 'No documents yet.'}
      </Typography>
      {showSelectFiles && (
        <AlineButton color="primary" variant="outlined" onClick={onSelectFiles}>
          Select files
        </AlineButton>
      )}
    </div>
  );
}

function DocumentRowForMode({
  activeId,
  canMoveBetweenRoles,
  documentVariant,
  file,
  sortableIds,
  workflowId,
}: {
  activeId: string | null;
  canMoveBetweenRoles: boolean;
  documentVariant: Apollo.DocumentVariant;
  file: WorkflowFileForList;
  workflowId: UUID;
  sortableIds?: string[];
}) {
  if (!canMoveBetweenRoles) {
    return <DocumentRowClickable file={file} workflowId={workflowId} />;
  }

  if (sortableIds?.includes(file.id)) {
    return (
      <SortableDocumentRow documentVariant={documentVariant} file={file} workflowId={workflowId} />
    );
  }

  return (
    <DraggableDocumentRow
      documentVariant={documentVariant}
      file={file}
      isBeingDragged={file.id === activeId}
      workflowId={workflowId}
    />
  );
}

interface EditableDocumentNameProps {
  commitRename: () => Promise<void>;
  isRenaming: boolean;
  linkTo: string;
  name: string;
  renameValue: string;
  setIsRenaming: (v: boolean) => void;
  setRenameValue: (v: string) => void;
  fileDocument?: StorableIconDocument;
  /** Certificate rows omit data-cy so e2e `documentName` matches a single user document. */
  includeWorkflowDocumentNameCy?: boolean;
  isSigning?: boolean;
}

function EditableDocumentName({
  commitRename,
  fileDocument,
  includeWorkflowDocumentNameCy = true,
  isRenaming,
  isSigning = false,
  linkTo,
  name,
  renameValue,
  setIsRenaming,
  setRenameValue,
}: EditableDocumentNameProps) {
  const { classes } = useDocumentListStyles();

  return (
    <div
      className={clsx(
        classes.documentName,
        classes.documentNameClickable,
        isSigning && classes.documentNameSigning
      )}
      style={{ alignItems: 'center', display: 'flex', flex: 1, gap: 6, minWidth: 0 }}
    >
      {fileDocument && (
        <StorableIcon iconProps={{ height: 18, width: 18 }} storable={fileDocument} />
      )}
      {isRenaming ? (
        <EditableNameField
          commitRename={commitRename}
          isSigning={isSigning}
          renameValue={renameValue}
          setIsRenaming={setIsRenaming}
          setRenameValue={setRenameValue}
        />
      ) : (
        <Link
          className={classes.stretchedLink}
          data-cy={
            includeWorkflowDocumentNameCy
              ? Select.workflow.detail.documents.documentName
              : undefined
          }
          to={linkTo}
          onClick={e => openLinkFromAnchorClick(e, linkTo)}
        >
          <Typography
            className={classes.documentName}
            style={isSigning ? { fontWeight: 700 } : undefined}
            variant="body2"
          >
            {name}
          </Typography>
        </Link>
      )}
    </div>
  );
}

/** Rendered in place of the document link while the row is being renamed. */
function EditableNameField({
  commitRename,
  isSigning,
  renameValue,
  setIsRenaming,
  setRenameValue,
}: {
  commitRename: () => Promise<void>;
  isSigning: boolean;
  renameValue: string;
  setIsRenaming: (v: boolean) => void;
  setRenameValue: (v: string) => void;
}) {
  const { classes } = useDocumentListStyles();

  return (
    <InlineEditInput
      autoFocus
      classes={{
        input: isSigning ? classes.documentNameInputSigning : classes.documentNameInput,
      }}
      value={renameValue}
      onBlur={() => void commitRename()}
      onChange={e => setRenameValue(e.target.value)}
      onClick={e => e.stopPropagation()}
      onKeyDown={e => {
        if (e.key === 'Enter') void commitRename();
        if (e.key === 'Escape') setIsRenaming(false);
      }}
      onMouseDown={e => e.stopPropagation()}
    />
  );
}
